import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "yu_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}
const STRUCT_KEY = "yu_active_structure";
export const getActiveStructure = () => sessionStorage.getItem(STRUCT_KEY) || "";
export function setActiveStructure(id) {
  if (id) sessionStorage.setItem(STRUCT_KEY, id);
  else sessionStorage.removeItem(STRUCT_KEY);
}
export function setToken(t) {
  setActiveStructure("");
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const http = axios.create({ baseURL: BASE });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  const sid = getActiveStructure();
  if (sid) config.headers["X-Structure-Id"] = sid;
  return config;
});

function niceError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  let detail = (data && (data.detail || data.message)) || "";
  if (typeof detail !== "string") detail = "";
  detail = detail.trim();
  if (!detail || detail.startsWith("<") || (status >= 500)) {
    detail = "Servizio momentaneamente non disponibile, riprova tra qualche secondo";
  }
  const err = new Error(detail);
  err.status = status;
  return err;
}

async function run(promise) {
  try {
    const res = await promise;
    return res.data;
  } catch (e) {
    throw niceError(e);
  }
}

export const api = {
  // auth
  login: (email, password) => run(http.post("/auth/login", { email, password })),
  me: () => run(http.get("/auth/me")),
  changeOwnPassword: (current_password, new_password) =>
    run(http.patch("/auth/me/password", { current_password, new_password })),
  listTeam: () => run(http.get("/admin/team")),
  createTeamMember: (email, password) => run(http.post("/admin/team", { email, password })),
  resetTeamPassword: (uid, password) => run(http.post(`/admin/team/${uid}/password`, { password })),
  deleteTeamMember: (uid) => run(http.delete(`/admin/team/${uid}`)),
  checkPasswordToken: (token) => run(http.get(`/auth/set-password/${token}`)),
  setPassword: (token, password) => run(http.post("/auth/set-password", { token, password })),

  // client
  myOverview: () => run(http.get("/me/overview")),
  myPeriods: (period_type = "monthly") => run(http.get(`/me/periods?period_type=${period_type}`)),
  myMessages: () => run(http.get("/me/messages")),
  sendMessage: (text) => run(http.post("/me/messages", { text })),
  replyMessage: (mid, text) => run(http.post(`/me/messages/${mid}/reply`, { text })),

  // admin
  summary: () => run(http.get("/admin/summary")),
  listStructures: () => run(http.get("/admin/structures")),
  createStructure: (body) => run(http.post("/admin/structures", body)),
  getStructure: (id) => run(http.get(`/admin/structures/${id}`)),
  updateStructure: (id, body) => run(http.patch(`/admin/structures/${id}`, body)),
  archiveStructure: (id) => run(http.delete(`/admin/structures/${id}`)),
  adminOverview: (id) => run(http.get(`/admin/structures/${id}/overview`)),
  adminPeriods: (id, period_type = "monthly") =>
    run(http.get(`/admin/structures/${id}/periods?period_type=${period_type}`)),
  upsertPeriod: (id, body) => run(http.post(`/admin/structures/${id}/periods`, body)),
  importPeriodPdf: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return run(http.post("/admin/import/period-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  importForecastXlsx: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return run(http.post(`/admin/structures/${id}/import/forecast-xlsx`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  listAnnuals: (id) => run(http.get(`/admin/structures/${id}/annuals`)),
  setAnnual: (id, year, body) => run(http.put(`/admin/structures/${id}/annuals/${year}`, body)),
  aiAnalyze: (id) => run(http.post(`/admin/structures/${id}/ai/analyze`)),
  aiChatHistory: (id) => run(http.get(`/admin/structures/${id}/ai/chat`)),
  aiChat: (id, message) => run(http.post(`/admin/structures/${id}/ai/chat`, { message })),
  pricingGet: (id, year) => run(http.get(`/admin/structures/${id}/pricing/${year}`)),
  pricingSave: (id, year, body) => run(http.put(`/admin/structures/${id}/pricing/${year}`, body)),
  pricingGenerate: (id, year, units, floors, ceilings, weekendPremium, instructions, file, oppFile) => {
    const fd = new FormData();
    fd.append("file", file);
    if (oppFile) fd.append("opp_file", oppFile);
    fd.append("year", year);
    fd.append("units", units.join("|"));
    fd.append("floors", JSON.stringify(floors || {}));
    fd.append("ceilings", JSON.stringify(ceilings || {}));
    fd.append("weekend_premium", weekendPremium);
    fd.append("instructions", instructions || "");
    return run(http.post(`/admin/structures/${id}/pricing/generate`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  pricingActualsImport: (id, year, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return run(http.post(`/admin/structures/${id}/pricing/${year}/actuals/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  pricingComparison: (id, year) => run(http.get(`/admin/structures/${id}/pricing/${year}/comparison`)),
  pricingRefine: (id, year) => run(http.post(`/admin/structures/${id}/pricing/${year}/refine`)),
  pricingJob: (id, jobId) => run(http.get(`/admin/structures/${id}/pricing/job/${jobId}`)),
  pricingRegenerate: (id, year, body) => run(http.post(`/admin/structures/${id}/pricing/${year}/regenerate`, body)),
  pricingPublish: (id, year, body) => run(http.post(`/admin/structures/${id}/pricing/${year}/publish`, body)),
  // specialist analysis published to client
  getClientAnalysis: (id) => run(http.get(`/admin/structures/${id}/client-analysis`)),
  setClientAnalysis: (id, body) => run(http.put(`/admin/structures/${id}/client-analysis`, body)),
  previewPricing: (id) => run(http.get(`/admin/structures/${id}/preview/pricing`)),
  previewOpportunities: (id) => run(http.get(`/admin/structures/${id}/preview/opportunities`)),
  // sales opportunities
  getOpportunities: (id, year) => run(http.get(`/admin/structures/${id}/opportunities/${year}`)),
  setOpportunities: (id, year, body) => run(http.put(`/admin/structures/${id}/opportunities/${year}`, body)),
  generateOpportunities: (id, year) => run(http.post(`/admin/structures/${id}/opportunities/${year}/generate`)),
  adminAirRefresh: (id, year) => run(http.post(`/admin/structures/${id}/opportunities/${year}/air/refresh`)),
  myOpportunities: () => run(http.get("/me/opportunities")),
  myAirRefresh: (year) => run(http.post(`/me/opportunities/${year}/air/refresh`)),
  // client pricing
  myPricing: () => run(http.get("/me/pricing")),
  pricingFeedback: (year, body) => run(http.post(`/me/pricing/${year}/feedback`, body)),
  deletePeriod: (id, pid) => run(http.delete(`/admin/structures/${id}/periods/${pid}`)),
  setFocus: (id, body) => run(http.put(`/admin/structures/${id}/focus`, body)),
  setSpecialistNote: (id, body) => run(http.put(`/admin/structures/${id}/specialist-note`, body)),
  getStartingData: (id) => run(http.get(`/admin/structures/${id}/starting-data`)),
  setStartingData: (id, body) => run(http.put(`/admin/structures/${id}/starting-data`, body)),
  setCompetitors: (id, competitors) => run(http.put(`/admin/structures/${id}/competitors`, { competitors })),
  createClient: (body) => run(http.post("/admin/clients", body)),
  resetClientPassword: (uid, password) => run(http.post(`/admin/clients/${uid}/password`, { password })),
  resendWelcome: (uid) => run(http.post(`/admin/clients/${uid}/resend-welcome`)),
  updateClientEmail: (uid, email) => run(http.patch(`/admin/clients/${uid}`, { email })),
  deleteClient: (uid, structure_id) => run(http.delete(`/admin/clients/${uid}${structure_id ? `?structure_id=${structure_id}` : ""}`)),
  linkClient: (sid, email) => run(http.post(`/admin/structures/${sid}/clients/link`, { email })),
  adminMessages: (structure_id) =>
    run(http.get(`/admin/messages${structure_id ? `?structure_id=${structure_id}` : ""}`)),
  setMessageStatus: (mid, status) => run(http.patch(`/admin/messages/${mid}`, { status })),
  adminReplyMessage: (mid, text) => run(http.post(`/admin/messages/${mid}/reply`, { text })),

  // partner (self)
  partnerDashboard: () => run(http.get("/partner/dashboard")),
  partnerReferrals: () => run(http.get("/partner/referrals")),
  partnerCreateReferral: (body) => run(http.post("/partner/referrals", body)),
  partnerAddNote: (rid, text) => run(http.post(`/partner/referrals/${rid}/note`, { text })),

  // admin partners & referrals
  adminPartners: () => run(http.get("/admin/partners")),
  adminCreatePartner: (body) => run(http.post("/admin/partners", body)),
  adminUpdatePartner: (pid, body) => run(http.patch(`/admin/partners/${pid}`, body)),
  adminTogglePartner: (pid) => run(http.post(`/admin/partners/${pid}/toggle-active`)),
  adminResendPartnerInvite: (pid) => run(http.post(`/admin/partners/${pid}/resend-invite`)),
  adminReferrals: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return run(http.get(`/admin/referrals${qs ? `?${qs}` : ""}`));
  },
  adminUpdateReferral: (rid, body) => run(http.patch(`/admin/referrals/${rid}`, body)),
};

export const homeForRole = (role) =>
  role === "ADMIN" ? "/admin" : role === "PARTNER" ? "/partner" : "/client/dashboard";

// Authenticated PDF download (blob) with a triggered save.
export async function downloadPdf(path, filename) {
  const res = await http.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "report.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default http;
